import type { SessionState, Rotation, AnimationDefsMap, ViewportManager, ScrubAnimationDef } from './types'
import type { BreakpointRange, AnimationDef } from '@wix/thunderbolt-becky-types'
import type { AnimatorManager, ScrollManager, ScrubSceneFactoryMap } from 'feature-animations'
import { TriggerVariant } from '@wix/thunderbolt-becky-types'

const CLEAR_PROPS = 'clip,clipPath,webkitClipPath,willChange,opacity,transform,transformOrigin,filter'
const getStateId = (id: string) => `${id}-motionEffects`

type Triggers = {
	play?: Array<{ effectId: string; targetId: string }>
	hold?: Array<[string, Array<string>]>
	resume?: Array<[string, Array<string>]>
} & {
	scrub?: Record<string, TriggerVariant>
}

export class MotionEffectsManager {
	private animator: AnimatorManager
	private definitions: AnimationDefsMap
	private scrubScenes: ScrubSceneFactoryMap
	private callbacks: Record<string, { end?: Array<(event: any) => void>; start?: Array<(event: any) => void> }>
	private sessionState: SessionState
	private _resizeRegistered: boolean
	private scrollManagers: Array<ScrollManager>
	public viewportWidth: number
	public motionViewport: ViewportManager | undefined

	constructor(animationsManager: AnimatorManager, isReponsive: boolean) {
		this.viewportWidth = isReponsive ? window.innerWidth : 0
		this.animator = animationsManager
		this.definitions = {}
		this.scrubScenes = {}
		this.callbacks = {}
		this.sessionState = {
			played: new Map(),
			running: new Map(),
		}
		this._resizeRegistered = false
		this.scrollManagers = []
	}

	_shouldSkipPlayedAnimation(stateId: string) {
		const wasPlayedInSession = this.sessionState.played.has(stateId)
		const { playOnce, persistOnNav } = this.sessionState.played.get(stateId) || {}
		return wasPlayedInSession && (playOnce || persistOnNav)
	}

	setAnimationStartState(compId: string | HTMLElement) {
		const element: any = typeof compId === 'string' ? document.getElementById(compId) : compId
		if (element) {
			element.dataset.motionEnter = 'done'
		}
	}

	_addAnimatingClass(targetId: string) {
		const el = document.getElementById(targetId)
		if (el) {
			el.classList.add('is-animating')
			this.setAnimationStartState(el)
		}
	}

	_removeAnimatingClass(targetId: string) {
		const el = document.getElementById(targetId)
		if (el) {
			el.classList.remove('is-animating')
		}
	}

	updateDefinitions(animationDefsMap: AnimationDefsMap) {
		Object.assign(this.definitions, animationDefsMap)

		const newEffectIds = Object.keys(animationDefsMap)

		this.updateScrubScenes(newEffectIds)
	}

	updateScrubScenes(effectIds: Array<string>) {
		const scrubAnimations: Record<string, ScrubAnimationDef> = {}

		for (const effectId of effectIds) {
			const animation = this._getEffectVariationForCurrentBreakpoint(effectId)

			if ((animation as ScrubAnimationDef).type === 'ScrubAnimationOptions') {
				scrubAnimations[effectId] = animation as ScrubAnimationDef
			}
		}

		this.scrubScenes = Object.assign(this.scrubScenes, this.animator.createScrubAnimations(scrubAnimations))
	}

	updateScrubManagers(triggers: Triggers['scrub'] = {}, clearExisting: boolean = false) {
		if (this.scrollManagers.length && clearExisting) {
			this.scrollManagers.forEach((manager) => manager.destroy())
			this.scrollManagers.length = 0

			const targetIds = new Set() as Set<string>

			// collect target ids and kill all animations
			for (const effectId in this.scrubScenes) {
				targetIds.add(this.scrubScenes[effectId].targetId)
				this.scrubScenes[effectId].animation?.kill()
			}
			this.viewportWidth = window.innerWidth
			this.animator.clearScrubAnimations(targetIds)
			this.updateScrubScenes(Object.keys(this.definitions))
		}

		//  create new Scroll managers
		this.scrollManagers.push(...this.animator.startScrubAnimations(triggers, this.scrubScenes))
	}

	/**
	 * Add data-angle attr to component before animating to handle comp rotation
	 */
	handleRotation(el: HTMLElement, rotation: Rotation) {
		el.dataset.angle = String(rotation)
		el.dataset.angleStyleLocation = 'style'
	}

	/**
	 * Trigger screenIn on all trigger descriptors passed
	 */
	trigger(triggers: Triggers = {}, clearExisting?: boolean) {
		if (triggers.scrub) {
			this.updateScrubManagers(triggers.scrub, clearExisting)
		}

		if (triggers.play?.length) {
			triggers.play.forEach(({ effectId, targetId }) => {
				const animation = this._getEffectVariationForCurrentBreakpoint(effectId) as AnimationDef

				if (animation.name) {
					this.playAnimation(animation as AnimationDef, effectId, { targetId })
				}
			})
		}

		if (triggers.resume?.length) {
			triggers.resume.forEach(([targetId, effectIds]) => {
				for (const effectId of effectIds) {
					const animation = this._getEffectVariationForCurrentBreakpoint(effectId) as AnimationDef

					if (animation.name) {
						this.resumeOrPlayAnimation(animation as AnimationDef, effectId, { targetId })
					}
				}
			})
		}

		if (triggers.hold?.length) {
			triggers.hold.forEach(([targetId, effectIds]) => {
				for (const effectId of effectIds) {
					const animation = this._getEffectVariationForCurrentBreakpoint(effectId) as AnimationDef

					if (animation.name) {
						this.pauseAnimation({ ...animation, targetId } as AnimationDef)
					}
				}
			})
		}
	}

	init(animationDefs: AnimationDefsMap) {
		this.stopAnimations()
		this.definitions = {}
		this.updateDefinitions(animationDefs)
	}

	addDefinition(animationDef: AnimationDefsMap, compElement: HTMLElement, compRotation: Rotation) {
		this.handleRotation(compElement, compRotation)
		this.updateDefinitions(animationDef)
	}

	addEffectCallback(effectId: string, triggerType: string, callback: () => void) {
		const eventName = triggerType === 'animation-end' ? 'end' : 'start'
		this.callbacks[effectId] = this.callbacks[effectId] || { end: [], start: [] }
		this.callbacks[effectId][eventName]?.push(callback)
	}

	clearEffectCallbacks(effectId: string) {
		delete this.callbacks[effectId]
	}

	observeTargetPlaybackInViewport(effectId: string, targetId: string) {
		const targetElement = document.getElementById(targetId)
		if (targetElement && this.motionViewport) {
			this.motionViewport.observe(targetElement, effectId)
		}
	}

	/**
	 * Play an animation
	 */
	playAnimation(
		animation: Omit<AnimationDef, 'action'>,
		effectId: string,
		overrides: Partial<Omit<AnimationDef, 'action'>> = {}
	) {
		const updatedAnimation = { ...animation, ...overrides }
		const { targetId, iterations } = updatedAnimation
		const stateId = getStateId(targetId)
		// Skip if played in current page or played in current session and marked as play once
		if (this._shouldSkipPlayedAnimation(stateId)) {
			this.setAnimationStartState(targetId)
			return
		}

		if (iterations === 0) {
			this.observeTargetPlaybackInViewport(effectId, targetId)
			return
		}

		this._createAndRunSequence(updatedAnimation, effectId)
	}

	_createAndRunSequence(animation: Omit<AnimationDef, 'action'>, effectId: string = '') {
		const { name, targetId, duration, delay, easing, params, playOnce, persistOnNav, iterations = 1 } = animation
		const stateId = getStateId(targetId)

		const clearParams = { props: CLEAR_PROPS, immediateRender: false }
		const animationData = { name, targetId, duration, delay, params: { ...params, easing } }

		const baseClearData = {
			name: 'BaseClear',
			targetId,
			duration: 0,
			delay: 0,
			params: clearParams,
		}

		this.animator.runSequence(
			[
				{ type: 'Animation', data: animationData },
				{ type: 'Animation', data: baseClearData },
			],
			{
				repeat: iterations - 1,
				callbacks: {
					onStart: (instance: any) => {
						this._addAnimatingClass(targetId)
						this.sessionState.running.set(instance, { targetId })
						effectId &&
							this.callbacks[effectId]?.start?.forEach((callback) => callback({ compId: targetId }))
					},
					onComplete: (instance: any) => {
						this._removeAnimatingClass(targetId)
						this.sessionState.running.delete(instance)
						effectId && this.callbacks[effectId]?.end?.forEach((callback) => callback({ compId: targetId }))
					},
					onInterrupt: (instance: any) => {
						this._removeAnimatingClass(targetId)
						this.sessionState.running.delete(instance)
					},
				},
			}
		)

		this.sessionState.played.set(stateId, { playOnce, persistOnNav })
	}

	_getEffectVariationForCurrentBreakpoint(effectId: string) {
		const defaultVariation = this.definitions[effectId].find((variation) => !variation.variants?.length) as
			| AnimationDef
			| ScrubAnimationDef

		if (this.viewportWidth) {
			return (
				// @ts-expect-error
				this.definitions[effectId].findLast((variation) => {
					return (variation.variants as Array<BreakpointRange>)?.some((variant) => {
						if (variant.max && variant.max < this.viewportWidth) {
							return false
						}

						if (variant.min && variant.min > this.viewportWidth) {
							return false
						}

						return true
					})
				}) || defaultVariation
			)
		}

		return defaultVariation
	}

	/**
	 * kill all running animations
	 */
	stopAnimations({ skipPersistent = true }: { skipPersistent?: boolean } = {}) {
		this.sessionState.running.forEach(({ targetId }, animation) => {
			const stateId = getStateId(targetId)
			// If animation is marked as persistent (ie. master page)
			const { persistOnNav } = this.sessionState.played.get(stateId) || {}
			const isPersistentToSkip = skipPersistent && persistOnNav
			if (!isPersistentToSkip) {
				this.animator.kill(animation, 1)
			}
		})

		this._killScrubAnimations()
	}

	getSequenceFromState(animationDef: Omit<AnimationDef, 'action'>) {
		let targetSequence: any
		this.sessionState.running.forEach(({ targetId }, animation) => {
			if (targetId === animationDef.targetId) {
				targetSequence = animation
			}
		})
		return targetSequence
	}

	/**
	 * resume a paused animation or create and run an animation if it doesn't exist in cache
	 */
	resumeOrPlayAnimation(
		animation: Omit<AnimationDef, 'action'>,
		effectId: string,
		overrides: Partial<Omit<AnimationDef, 'action'>> = {}
	) {
		const updateAnimation = { ...animation, ...overrides }
		const targetSequence = this.getSequenceFromState(updateAnimation)
		targetSequence ? targetSequence.play() : this._createAndRunSequence(updateAnimation, effectId)
	}

	/**
	 * pause a running animation
	 */
	pauseAnimation(animationDef: Omit<AnimationDef, 'action'>) {
		const targetSequence = this.getSequenceFromState(animationDef)
		targetSequence?.pause()
	}

	_killScrubAnimations() {
		if (this.scrollManagers.length) {
			this.scrollManagers.forEach((manager) => manager.destroy())
			this.scrollManagers.length = 0
		}
	}

	clearAnimations() {
		this.definitions = {}

		this._killScrubAnimations()
	}

	clearState() {
		this.sessionState.played.clear()
		this.sessionState.running.clear()
	}
}
